import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { formatRelative } from "../OrgOverview/format";
import { fetchEvalReport, fetchEvalRun, fetchEvalRuns } from "./api";
import {
  STAGE_LABELS,
  STAGE_ORDER,
  formatDelta,
  formatMoney,
} from "./evalOutput";
import LoanDriftDetail from "./LoanDriftDetail";
import MetricsStrip from "./MetricsStrip";
import RunLauncher from "./RunLauncher";
import "./ModelSwitchEval.css";

const ENV_KEY = "mse-env";

const ENVIRONMENTS = [
  { id: "dev", label: "Dev" },
  { id: "prod", label: "Prod" },
];

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "active", label: "Running" },
  { id: "succeeded", label: "Succeeded" },
  { id: "failed", label: "Failed" },
];

const StatusPill = ({ status }) => (
  <span className={`mse-pill mse-pill--${status || "queued"}`}>
    {(status || "queued").replace("-", " ")}
  </span>
);

const SeverityChip = ({ severity }) => {
  if (severity == null) {
    return <span className="mse-sev mse-sev--na">n/a</span>;
  }

  return <span className={`mse-sev mse-sev--${severity}`}>{severity}</span>;
};

function EnvToggle({ value, onChange }) {
  return (
    <div className="mse-segmented" role="radiogroup" aria-label="Environment">
      {ENVIRONMENTS.map((entry) => (
        <button
          key={entry.id}
          type="button"
          role="radio"
          aria-checked={value === entry.id}
          className={`mse-segmented__btn ${
            value === entry.id ? "mse-segmented__btn--active" : ""
          }`}
          onClick={() => onChange(entry.id)}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}

/** Two model names read as a comparison, not a sentence. */
function ModelPair({ baseline, candidate }) {
  return (
    <span className="mse-modelpair">
      <span className="mse-modelpair__from">{baseline}</span>
      <span className="mse-modelpair__arrow" aria-hidden="true">
        →
      </span>
      <span className="mse-modelpair__to">{candidate}</span>
    </span>
  );
}

function RunListSkeleton() {
  return (
    <div className="mse-skeletons" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="mse-skeleton" />
      ))}
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="mse-empty">
      <strong className="mse-empty__title">{title}</strong>
      <p className="mse-empty__body">{body}</p>
    </div>
  );
}

/**
 * Everything about the run that is not the decision: which models ran, how they
 * were wired, and the raw drift counts behind the metrics above.
 */
function RunOverview({ report }) {
  if (!report?.summary) {
    return null;
  }
  const { summary, config } = report;
  const headline = summary.headline || {};

  const executionLabel =
    config?.executionMode === "production_flow_no_write"
      ? "Real production flow · no writes"
      : config?.chainStep3
        ? "Connected Step 1 → Step 3"
        : "Legacy isolated Step 3";

  const chips = [
    { label: "loans", value: headline.loansEvaluated ?? summary.totalCases },
    { label: "income moved", value: headline.incomeMismatches ?? 0 },
    { label: "status flips", value: headline.statusFlips ?? 0 },
    { label: "doc selection drift", value: headline.docSelectionDrift ?? 0 },
    { label: "Step 3 monetary drift", value: headline.step3MonetaryDrift ?? 0 },
  ];

  return (
    <>
      <MetricsStrip summary={summary} />

      <div className="mse-runinfo">
        <div className="mse-runinfo__item">
          <span className="mse-stat-label">Comparison</span>
          <ModelPair
            baseline={config?.baselineModel || "baseline"}
            candidate={config?.candidateModel || "candidate"}
          />
          <span className="mse-muted-xs">
            {config?.baselineProvider || "—"} vs{" "}
            {config?.candidateProvider || "—"}
          </span>
        </div>
        <div className="mse-runinfo__item">
          <span className="mse-stat-label">Execution</span>
          <span
            className={
              config?.chainStep3
                ? "mse-chain-state mse-chain-state--connected"
                : "mse-chain-state mse-chain-state--isolated"
            }
          >
            {executionLabel}
          </span>
        </div>
        <div className="mse-runinfo__item">
          <span className="mse-stat-label">Severity spread</span>
          <span className="mse-sevbar">
            <span className="mse-sev mse-sev--high">
              {summary.bySeverity?.high ?? 0} high
            </span>
            <span className="mse-sev mse-sev--medium">
              {summary.bySeverity?.medium ?? 0} med
            </span>
            <span className="mse-sev mse-sev--low">
              {summary.bySeverity?.low ?? 0} low
            </span>
          </span>
        </div>
      </div>

      <div className="mse-statchips">
        {chips.map((chip) => (
          <span key={chip.label} className="mse-statchip">
            <b>{chip.value ?? "—"}</b> {chip.label}
          </span>
        ))}
      </div>
    </>
  );
}

function GoldenLoanTable({ loanGroups, onSelect }) {
  if (!loanGroups?.length) {
    return (
      <EmptyState
        title="No loans in this report"
        body="The run finished without producing any comparable loan groups."
      />
    );
  }

  return (
    <div className="mse-table-scroll">
      <table className="mse-table">
        <thead>
          <tr>
            <th>Loan</th>
            <th>Borrower</th>
            <th>Type</th>
            {STAGE_ORDER.map((stageId) => (
              <th key={stageId}>{STAGE_LABELS[stageId]}</th>
            ))}
            <th>Income</th>
            <th>Docs</th>
          </tr>
        </thead>
        <tbody>
          {loanGroups.map((group) => {
            const income = group.headline?.qualifyingMonthlyIncome;
            const docs = group.headline?.docSelection;

            return (
              <tr
                key={group.loanId}
                className="mse-row"
                onClick={() => onSelect(group.loanId)}
              >
                <td className="mse-mono">
                  {group.loanUrl ? (
                    <a
                      href={group.loanUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {group.loanId.slice(0, 8)}…
                    </a>
                  ) : (
                    `${group.loanId.slice(0, 8)}…`
                  )}
                </td>
                <td>{group.borrower || "—"}</td>
                <td>{group.loanType || "—"}</td>
                {STAGE_ORDER.map((stageId) => (
                  <td key={stageId}>
                    <SeverityChip
                      severity={group.stageSeverity?.[stageId] ?? null}
                    />
                  </td>
                ))}
                <td>
                  {income ? (
                    <>
                      {formatMoney(income.baseline)} →{" "}
                      {formatMoney(income.candidate)}
                      {income.changed && (
                        <span className="mse-cell-note">
                          {formatDelta(income.delta)}
                        </span>
                      )}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {docs
                    ? `${docs.candidateCount ?? "—"} / ${docs.baselineCount ?? "—"}`
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RunDetail({ runId, env, onReady, onChildRunQueued }) {
  const history = useHistory();
  const location = useLocation();
  const selectedLoanId = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return params.get("loanId") || null;
  }, [location.search]);

  const [run, setRun] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const runRes = await fetchEvalRun(runId, env);
        if (!runRes.success && !runRes.id && !runRes.run) {
          throw new Error(runRes.reason || "Failed to load run.");
        }
        const nextRun = runRes.run
          ? { ...runRes.run, reportUrls: runRes.reportUrls }
          : runRes;
        setRun(nextRun);

        if (["succeeded", "failed"].includes(nextRun.status)) {
          try {
            const reportRes = await fetchEvalReport(runId, env);
            if (reportRes.success) {
              setReport(reportRes.report);
            } else if (reportRes.reason) {
              // 409 while still writing artifacts — keep polling.
              setReport(null);
            }
          } catch (reportErr) {
            if (!String(reportErr.message || "").includes("409")) {
              throw reportErr;
            }
          }
        } else {
          setReport(null);
        }
      } catch (err) {
        setError(err.message);
        if (!silent) {
          toast.error(err.message);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
        onReady?.();
      }
    },
    [env, onReady, runId],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const active =
      ["queued", "running"].includes(run?.status) ||
      (run?.status === "succeeded" && !report);
    if (!active) {
      return undefined;
    }
    const timer = window.setInterval(() => load({ silent: true }), 10000);

    return () => window.clearInterval(timer);
  }, [load, report, run?.status]);

  const loanGroup = useMemo(() => {
    if (!selectedLoanId || !report?.loanGroups) {
      return null;
    }

    return (
      report.loanGroups.find((group) => group.loanId === selectedLoanId) || null
    );
  }, [report, selectedLoanId]);

  const selectLoan = (loanId) => {
    history.push(`/model-switch-eval/${runId}?env=${env}&loanId=${loanId}`);
  };

  const clearLoan = () => {
    history.push(`/model-switch-eval/${runId}?env=${env}`);
  };

  if (loading && !run) {
    return <RunListSkeleton />;
  }

  if (error && !run) {
    return <div className="mse-error">{error}</div>;
  }

  if (selectedLoanId && loanGroup) {
    return (
      <LoanDriftDetail
        runId={runId}
        env={env}
        loanGroup={loanGroup}
        cases={report?.cases || []}
        config={report?.config}
        onBack={clearLoan}
        onChildRunQueued={onChildRunQueued}
      />
    );
  }

  const models = run?.resolvedModels || {};
  const isInFlight = ["queued", "running"].includes(run?.status);

  return (
    <div className="mse-run-detail">
      <button
        type="button"
        className="mse-btn mse-back"
        onClick={() => history.push(`/model-switch-eval?env=${env}`)}
      >
        ← All runs
      </button>

      <div className="mse-run-detail__header">
        <div>
          <h2 className="mse-run-detail__title">
            <ModelPair
              baseline={
                models.baselineModel || run?.config?.baselineModel || "baseline"
              }
              candidate={
                models.candidateModel ||
                run?.config?.candidateModel ||
                "candidate"
              }
            />
            <StatusPill status={run?.status} />
          </h2>
          <p className="mse-subtitle">
            Run <code className="mse-mono">{runId}</code>
            {run?.parentRunId && (
              <>
                {" · child of "}
                <button
                  type="button"
                  className="mse-link-btn"
                  onClick={() =>
                    history.push(
                      `/model-switch-eval/${run.parentRunId}?env=${env}`,
                    )
                  }
                >
                  {run.parentRunId}
                </button>
              </>
            )}
          </p>
        </div>
        <div className="mse-controls">
          {run?.reportUrls?.html && (
            <a
              className="mse-btn"
              href={run.reportUrls.html}
              target="_blank"
              rel="noreferrer"
            >
              Raw HTML report
            </a>
          )}
          <button
            type="button"
            className="mse-btn"
            onClick={() => load()}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="mse-error">{error}</div>}

      {isInFlight && (
        <div className="mse-progress">
          <span className="mse-progress__bar" />
          <span className="mse-progress__text">
            Replaying both models — refreshing every 10 seconds.
          </span>
        </div>
      )}

      {report && (
        <>
          <RunOverview report={report} />
          <h3 className="mse-section-title">Loans in this run</h3>
          <GoldenLoanTable
            loanGroups={report.loanGroups}
            onSelect={selectLoan}
          />
        </>
      )}

      {!report && run?.status === "succeeded" && (
        <EmptyState
          title="Report still being written"
          body="The run finished and the artifacts are being uploaded. This refreshes automatically."
        />
      )}

      {run?.error && <div className="mse-error-card">{run.error}</div>}
    </div>
  );
}

function matchesStatusFilter(run, statusFilter) {
  if (statusFilter === "all") {
    return true;
  }
  if (statusFilter === "active") {
    return ["queued", "running"].includes(run.status);
  }

  return run.status === statusFilter;
}

function matchesSearch(run, needle) {
  if (!needle) {
    return true;
  }
  const haystack = [
    run.id,
    run.config?.baselineModel,
    run.config?.candidateModel,
    run.resolvedModels?.baselineModel,
    run.resolvedModels?.candidateModel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(needle);
}

export default function ModelSwitchEvalPage({ onReady }) {
  const [theme] = useDarkMode();
  const history = useHistory();
  const location = useLocation();
  const { runId } = useParams();

  const envFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return params.get("env");
  }, [location.search]);

  const [env, setEnv] = useState(
    () => envFromQuery || localStorage.getItem(ENV_KEY) || "dev",
  );
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (envFromQuery && envFromQuery !== env) {
      setEnv(envFromQuery);
    }
  }, [env, envFromQuery]);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const res = await fetchEvalRuns({ limit: 40 }, env);
        if (res.success) {
          setRuns(Array.isArray(res.runs) ? res.runs : []);
        } else {
          setError(res.reason || "Failed to load runs.");
        }
      } catch (err) {
        setError(err.message);
        if (!silent) {
          toast.error(err.message);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
        onReady?.();
      }
    },
    [env, onReady],
  );

  useEffect(() => {
    if (runId) {
      onReady?.();

      return;
    }
    load();
  }, [env, load, onReady, runId]);

  useEffect(() => {
    if (runId) {
      return undefined;
    }
    const hasActive = runs.some((run) =>
      ["queued", "running"].includes(run.status),
    );
    if (!hasActive) {
      return undefined;
    }
    const timer = window.setInterval(() => load({ silent: true }), 10000);

    return () => window.clearInterval(timer);
  }, [load, runId, runs]);

  const visibleRuns = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return runs.filter(
      (run) =>
        matchesStatusFilter(run, statusFilter) && matchesSearch(run, needle),
    );
  }, [runs, search, statusFilter]);

  const onEnvChange = (next) => {
    localStorage.setItem(ENV_KEY, next);
    setEnv(next);
    if (runId) {
      history.replace(`/model-switch-eval/${runId}?env=${next}`);
    }
  };

  const openRun = (id) => {
    history.push(`/model-switch-eval/${id}?env=${env}`);
  };

  const onQueued = (result) => {
    setRuns((current) => [
      {
        id: result.runId,
        status: result.status || "queued",
        config: result.config,
        createdAt: Date.now(),
        parentRunId: result.parentRunId || null,
        childRunIds: [],
      },
      ...current.filter((run) => run.id !== result.runId),
    ]);
    if (result.runId) {
      openRun(result.runId);
    }
  };

  return (
    <div className="mse-page" data-theme={theme}>
      <ToastContainer position="bottom-right" theme={theme} />
      <div className="mse-header">
        <div>
          <h1 className="mse-title">Evals</h1>
          <p className="mse-subtitle">
            Compare the production model against a candidate on one loan, a
            golden dataset, or a loan cloned from production into dev.
          </p>
        </div>
        <div className="mse-controls">
          <EnvToggle value={env} onChange={onEnvChange} />
          {!runId && (
            <button
              type="button"
              className="mse-btn"
              onClick={() => load()}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
        </div>
      </div>

      {runId ? (
        <RunDetail
          runId={runId}
          env={env}
          onReady={onReady}
          onChildRunQueued={openRun}
        />
      ) : (
        <>
          <RunLauncher env={env} onQueued={onQueued} />

          {error && <div className="mse-error">{error}</div>}

          <div className="mse-listhead">
            <h3 className="mse-section-title">Recent runs</h3>
            <div className="mse-listhead__tools">
              <div className="mse-chips" role="tablist">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={statusFilter === filter.id}
                    className={`mse-chip ${
                      statusFilter === filter.id ? "mse-chip--active" : ""
                    }`}
                    onClick={() => setStatusFilter(filter.id)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <input
                className="mse-input mse-input--search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search run ID or model…"
                aria-label="Search runs"
              />
            </div>
          </div>

          {loading && !runs.length ? (
            <RunListSkeleton />
          ) : (
            <div className="mse-table-scroll">
              <table className="mse-table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Comparison</th>
                    <th>Stages</th>
                    <th className="mse-num">Cases</th>
                    <th className="mse-num">High sev</th>
                    <th>Created</th>
                    <th>Report</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRuns.map((run) => (
                    <tr
                      key={run.id}
                      className="mse-row"
                      onClick={() => openRun(run.id)}
                    >
                      <td className="mse-mono">
                        {run.id?.slice(0, 10)}…
                        {run.parentRunId && (
                          <span className="mse-cell-note">child run</span>
                        )}
                      </td>
                      <td>
                        <StatusPill status={run.status} />
                      </td>
                      <td>
                        <ModelPair
                          baseline={
                            run.resolvedModels?.baselineModel ||
                            run.config?.baselineModel ||
                            "baseline"
                          }
                          candidate={
                            run.resolvedModels?.candidateModel ||
                            run.config?.candidateModel ||
                            "—"
                          }
                        />
                      </td>
                      <td>
                        <span className="mse-stagechips">
                          {(run.config?.stageIds || []).length
                            ? run.config.stageIds.map((id) => (
                                <span key={id} className="mse-stagechip">
                                  {STAGE_LABELS[id] || id}
                                </span>
                              ))
                            : "—"}
                        </span>
                      </td>
                      <td className="mse-num">
                        {run.summary?.totalCases ?? "—"}
                      </td>
                      <td className="mse-num">
                        {run.summary?.bySeverity?.high ? (
                          <span className="mse-sev mse-sev--high">
                            {run.summary.bySeverity.high}
                          </span>
                        ) : (
                          (run.summary?.bySeverity?.high ?? "—")
                        )}
                      </td>
                      <td>{formatRelative(run.createdAt)}</td>
                      <td onClick={(event) => event.stopPropagation()}>
                        {run.reportUrls?.html ? (
                          <a
                            href={run.reportUrls.html}
                            target="_blank"
                            rel="noreferrer"
                            className="mse-link"
                          >
                            HTML
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                  {!loading && !visibleRuns.length && (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          title={
                            runs.length
                              ? "No runs match these filters"
                              : "No comparison runs yet"
                          }
                          body={
                            runs.length
                              ? "Clear the search or pick a different status."
                              : "Queue one above — a single loan is the fastest way to see the report."
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
